import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSpinner } from '@/fontawesome.ts';
import Modal from '@/components/ui/Modal.tsx';
import Checkbox from '@/components/ui/Checkbox';
import { APIRoutes } from '@/services/api.routes.ts';
import { handleAPIError, showSuccessToast, showErrorToast } from '@/utils/toasts.ts';
import { toCurrencyCentValue, toUSDCentValue, toRFCDateValue } from '@/utils/helpers.ts';
import type { RecommendationEarnings, EarningsRecommendationState } from '@/models';
import type { UpdateEarningsRecommendationRequest } from '@/modelsAPI';

interface EditEarningsRecommendationModalProps {
  isOpen: boolean;
  onClose: () => void;
  recommendation: RecommendationEarnings | null;
  onRecommendationUpdated: (recommendation: RecommendationEarnings) => void;
}

export default function EditEarningsRecommendationModal({
  isOpen,
  onClose,
  recommendation,
  onRecommendationUpdated
}: EditEarningsRecommendationModalProps) {
  const [formData, setFormData] = useState({
    strikePrice: '',
    startDate: '',
    endDate: '',
    premium: '',
    state: 'OPEN' as EarningsRecommendationState,
    boost: false,
    free: false
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (recommendation) {
      setFormData({
        strikePrice: (recommendation.strikePrice / 100).toString(),
        startDate: recommendation.startDate,
        endDate: recommendation.endDate,
        premium: (recommendation.premium / 100).toString(),
        state: recommendation.state,
        boost: recommendation.boost,
        free: recommendation.free
      });
    }
  }, [recommendation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!recommendation) return;

    // Validation
    if (parseFloat(formData.strikePrice) <= 0) {
      showErrorToast('Strike Preis muss größer als 0 sein');
      return;
    }

    if (parseFloat(formData.premium) <= 0) {
      showErrorToast('Prämie muss größer als 0 sein');
      return;
    }

    if (!formData.startDate || !formData.endDate) {
      showErrorToast('Start- und Enddatum sind erforderlich');
      return;
    }

    const startDate = new Date(formData.startDate);
    const endDate = new Date(formData.endDate);

    if (startDate >= endDate) {
      showErrorToast('Enddatum muss nach dem Startdatum liegen');
      return;
    }

    setLoading(true);

    try {
      const requestData: UpdateEarningsRecommendationRequest = {
        strikePrice: toCurrencyCentValue(parseFloat(formData.strikePrice)),
        startDate: toRFCDateValue(formData.startDate),
        endDate: toRFCDateValue(formData.endDate),
        premium: toUSDCentValue(parseFloat(formData.premium)),
        state: formData.state,
        boost: formData.boost,
        free: formData.free
      };

      const updatedRecommendation = await APIRoutes.updateEarningsRecommendation(recommendation.id, requestData);
      onRecommendationUpdated(updatedRecommendation);
      showSuccessToast('Earnings Recommendation erfolgreich aktualisiert');
      onClose();
    } catch (error) {
      handleAPIError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!recommendation) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Earnings Recommendation bearbeiten"
      icon={<FontAwesomeIcon icon={faEdit} className="w-5 h-5 text-green-600" />}
      maxWidth="lg"
      closeDisabled={loading}
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="strikePrice" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Strike Preis  *
            </label>
            <input
              type="number"
              id="strikePrice"
              step="0.01"
              value={formData.strikePrice}
              onChange={(e) => setFormData(prev => ({ ...prev, strikePrice: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
              disabled={loading}
              min="0"
            />
          </div>

          <div>
            <label htmlFor="premium" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Prämie / Kontrakt *
            </label>
            <input
              type="number"
              id="premium"
              step="0.01"
              value={formData.premium}
              onChange={(e) => setFormData(prev => ({ ...prev, premium: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
              disabled={loading}
              min="0"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Startdatum *
            </label>
            <input
              type="date"
              id="startDate"
              value={formData.startDate}
              onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Enddatum *
            </label>
            <input
              type="date"
              id="endDate"
              value={formData.endDate}
              onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
              disabled={loading}
            />
          </div>
        </div>

        <div>
          <label htmlFor="state" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Status
          </label>
          <select
            id="state"
            value={formData.state}
            onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value as EarningsRecommendationState }))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            disabled={loading}
          >
            <option value="OPEN">Offen</option>
            <option value="FINISHED_ASSIGNED">Eingebucht</option>
            <option value="FINISHED_UNASSIGNED">Beendet</option>
          </select>
        </div>

        <Checkbox
          checked={formData.boost}
          onChange={(checked) => setFormData(prev => ({ ...prev, boost: checked }))}
          disabled={loading}
          colorTheme="green"
          label="Als Boost-Trade markieren"
        />

        <Checkbox
          checked={formData.free}
          onChange={(checked) => setFormData(prev => ({ ...prev, free: checked }))}
          disabled={loading}
          colorTheme="green"
          label="Als kostenlosen Trade markieren"
        />

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                Speichern...
              </>
            ) : (
              'Speichern'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
